import { create } from 'zustand';
import type { CustomerSummaryDto, OrderDto, ProductSummaryDto } from '@ecom/contracts';
import { adminApi, withSession } from '@/lib/api-client';

/**
 * ⌘K's records (docs/07-Admin/Admin-App.md): orders by number, email or
 * phone; products by name or SKU; customers by name, email or phone — a few
 * of each, searched on the server by the lists' own endpoints, and only in
 * the areas this person may read.
 *
 * A late answer for an older query is dropped: typing "ORD-2" then "ORD-20"
 * must not end on the results for "ORD-2".
 */
export interface RecordHits {
  orders: OrderDto[];
  products: ProductSummaryDto[];
  customers: CustomerSummaryDto[];
}

const EMPTY: RecordHits = { orders: [], products: [], customers: [] };
const PER_KIND = 5;

interface SearchState {
  query: string;
  hits: RecordHits;
  searching: boolean;
  search: (query: string, may: { orders: boolean; products: boolean; customers: boolean }) => Promise<void>;
  clear: () => void;
}

export const useSearchStore = create<SearchState>((set, get) => ({
  query: '',
  hits: EMPTY,
  searching: false,

  async search(query, may) {
    const needle = query.trim();
    set({ query: needle });
    if (needle.length < 2) {
      set({ hits: EMPTY, searching: false });
      return;
    }
    set({ searching: true });

    const quietly = <T>(allowed: boolean, call: () => Promise<{ items: T[] }>): Promise<T[]> =>
      allowed ? withSession(call).then((page) => page.items, () => []) : Promise.resolve([]);

    const [orders, products, customers] = await Promise.all([
      quietly(may.orders, () => adminApi.orders.list({ search: needle, limit: PER_KIND })),
      quietly(may.products, () => adminApi.catalog.products({ search: needle, limit: PER_KIND })),
      quietly(may.customers, () => adminApi.customers.list({ search: needle, limit: PER_KIND })),
    ]);

    // Only the answer to what is in the box now.
    if (get().query !== needle) return;
    set({ hits: { orders, products, customers }, searching: false });
  },

  clear() {
    set({ query: '', hits: EMPTY, searching: false });
  },
}));
